import React, { useState, useEffect, useCallback } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { Activity, Database, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

function pingColor(ms, ok) {
  if (!ok || ms == null) return { dot: 'bg-rose-500', text: 'text-rose-600', label: 'Unreachable' };
  if (ms < 500) return { dot: 'bg-emerald-500', text: 'text-emerald-600', label: 'Healthy' };
  if (ms < 2000) return { dot: 'bg-amber-500', text: 'text-amber-600', label: 'Degraded' };
  return { dot: 'bg-rose-500', text: 'text-rose-600', label: 'Slow' };
}

const Card = ({ title, children, className = '' }) => (
  <div className={`bg-white border border-slate-200 rounded-sm p-4 ${className}`}>
    {title && <div className="font-display font-bold text-sm mb-3">{title}</div>}
    {children}
  </div>
);

export default function SystemHealth() {
  const [live, setLive] = useState(null);
  const [range, setRange] = useState(24); // hours
  const [latency, setLatency] = useState([]);
  const [busiest, setBusiest] = useState([]);
  const [modules, setModules] = useState([]);
  const [growth, setGrowth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async (isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const hoursParam = range <= 720 ? { hours: range } : {};
      const [l, lt, bh, sm, cg] = await Promise.all([
        apiGet('/system-health/live'),
        apiGet('/system-health/latency-timeseries', hoursParam),
        apiGet('/system-health/busiest-hours', { days: Math.max(1, Math.round(range / 24)) }),
        apiGet('/system-health/slow-modules', { hours: range }),
        apiGet('/system-health/collection-growth', { days: Math.max(1, Math.round(range / 24)) }),
      ]);
      setLive(l);
      setLatency((lt.points || []).map(p => ({ ...p, label: new Date(p.minute).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) })));
      setBusiest(bh.hours || []);
      setModules(sm.modules || []);
      setGrowth(cg.collections || []);
    } catch {
      toast.error('Failed to load system health data');
    }
    setLoading(false);
    setRefreshing(false);
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const status = live ? pingColor(live.ping_ms, live.ping_ok) : { dot: 'bg-slate-300', text: 'text-slate-400', label: '—' };
  const snap = live?.latest_snapshot;

  return (
    <div data-testid="system-health-page">
      <PageHeader
        overline="System Health"
        title="System Health · Performance"
        subtitle="Live response times, busiest hours, and DB growth trends — spot slowdowns before they become outages."
      />

      <div className="flex justify-end mb-3">
        <button data-testid="system-health-refresh" onClick={() => load(true)} disabled={refreshing}
          className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-sm border border-slate-200 hover:bg-slate-50 disabled:opacity-50">
          {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 py-8 text-center">Loading…</div>
      ) : (
        <div className="space-y-5">
          <Card>
            <div className="flex flex-wrap items-center gap-6" data-testid="system-health-live-strip">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${status.dot} animate-pulse`} />
                <span className={`font-display font-bold text-sm ${status.text}`}>{status.label}</span>
              </div>
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">DB Ping</div>
                <div className="text-lg font-display font-extrabold">{live?.ping_ms != null ? `${live.ping_ms} ms` : '—'}</div>
              </div>
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Connections</div>
                <div className="text-lg font-display font-extrabold">{snap?.connections_current ?? '—'} <span className="text-xs text-slate-400 font-normal">/ {snap?.connections_available ?? '—'} avail</span></div>
              </div>
              <div>
                <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">Mongo Memory (resident)</div>
                <div className="text-lg font-display font-extrabold">{snap?.mem_resident_mb ? `${fmt(snap.mem_resident_mb)} MB` : '—'}</div>
              </div>
              <div className="text-xs text-slate-400 ml-auto">Checked {live?.checked_at ? new Date(live.checked_at).toLocaleTimeString('en-IN') : '—'}</div>
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-3">
              <div className="font-display font-bold text-sm">Latency Trend</div>
              <div className="flex border border-slate-200 rounded-sm overflow-hidden">
                {[{ l: '24h', v: 24 }, { l: '7d', v: 24 * 7 }, { l: '30d', v: 24 * 30 }].map(o => (
                  <button key={o.v} data-testid={`system-health-range-${o.l}`} onClick={() => setRange(o.v)}
                    className={`px-3 py-1.5 text-xs font-semibold ${range === o.v ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-600'}`}>{o.l}</button>
                ))}
              </div>
            </div>
            {latency.length === 0 ? (
              <div className="text-sm text-slate-400 py-6 text-center">No latency data yet for this period — data accumulates every 5 minutes.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <AreaChart data={latency}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} unit="ms" />
                  <Tooltip formatter={(v) => `${v} ms`} />
                  <Area type="monotone" dataKey="avg_ms" name="Avg ms" stroke="#3b4872" fill="#3b4872" fillOpacity={0.15} />
                  <Area type="monotone" dataKey="max_ms" name="Max ms" stroke="#dc2626" fill="#dc2626" fillOpacity={0.05} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Card title="Busiest Hours">
              {busiest.every(h => h.count === 0) ? (
                <div className="text-sm text-slate-400 py-6 text-center">No traffic data yet for this period.</div>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={busiest}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="hour" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b4872" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card>

            <Card title="Slowest Modules">
              {modules.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center">No module data yet for this period.</div>
              ) : (
                <table className="w-full text-sm" data-testid="system-health-slow-modules-table">
                  <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                    <tr><th className="text-left py-1.5">Module</th><th className="text-right py-1.5">Avg ms</th><th className="text-right py-1.5">Max ms</th><th className="text-right py-1.5">Errors</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {modules.map(m => (
                      <tr key={m.module}>
                        <td className="py-1.5">{m.module}</td>
                        <td className="py-1.5 text-right">{m.avg_ms}</td>
                        <td className="py-1.5 text-right">{m.max_ms}</td>
                        <td className={`py-1.5 text-right ${m.error_count > 0 ? 'text-rose-600 font-semibold' : ''}`}>{m.error_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          </div>

          <Card title="Collection Growth (Watch-list)">
            <table className="w-full text-sm" data-testid="system-health-collection-growth-table">
              <thead className="text-[11px] uppercase tracking-wider text-slate-500">
                <tr><th className="text-left py-1.5">Collection</th><th className="text-right py-1.5">Current Count</th><th className="text-right py-1.5">Growth (period)</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {growth.map(c => (
                  <tr key={c.collection}>
                    <td className="py-1.5 flex items-center gap-2">
                      <Database size={13} className="text-slate-400" />
                      {c.collection}
                      {c.is_known_large && (
                        <span className="flex items-center gap-1 text-[10px] text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                          <AlertTriangle size={10} /> known large
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-right font-semibold">{fmt(c.current_count)}</td>
                    <td className={`py-1.5 text-right ${c.growth_in_period > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                      {c.growth_in_period > 0 ? '+' : ''}{fmt(c.growth_in_period)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}
    </div>
  );
}
